import React, { useEffect, useRef, useState, useCallback } from "react";
import icons from "./data/icons";
import { getFooter } from "./api/footerService";
import { getHome } from "./api/homeService";
import EmailLink from "./EmailLink";
import { getContact } from "./api/contactService";

const Footer = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [home, setHome] = useState(null);
    const [footer, setFooter] = useState(null);
    const [contactData, setContactData] = useState(null);

    const footerRef = useRef(null);

    // Smooth scroll handler
    const handleSmoothScroll = useCallback((e, sectionId) => {
        e.preventDefault();
        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }, []);
    useEffect(() => {
        const loadContact = async () => {
            try {
                const response = await getContact();

                const contact = response?.data?.data;

                setContactData(
                    Array.isArray(contact) ? contact[0] : contact
                );
            } catch (error) {
                console.error("Failed to load contact data:", error);
            }
        };

        loadContact();
    }, []);
    // Quick link click – supports scroll, projects, and route types
    const handlePortfolioNavigation = (e, link) => {
        e.preventDefault();

        const filter = link?.filter;

        if (!filter) {
            handleSmoothScroll(e, "projects");
            return;
        }

        // Remember filter when Projects component mounts
        sessionStorage.setItem("portfolioFilter", filter);

        // Go to Projects section
        handleSmoothScroll(e, "projects");

        // Apply filter
        window.dispatchEvent(
            new CustomEvent("portfolioFilterChange", {
                detail: { filter },
            })
        );

        // Notify Projects/Navbar
        window.dispatchEvent(
            new CustomEvent("navbarProjectsClick", {
                detail: { section: filter },
            })
        );
    };

    // Load Home data (only for footer logo)
    useEffect(() => {
        const loadHome = async () => {
            try {
                const res = await getHome();
                const raw = res?.data?.data ?? res?.data ?? null;
                const homeData = Array.isArray(raw) ? raw[0] : raw;
                setHome(homeData || null);
            } catch (error) {
                console.error("Failed to load home data:", error);
            }
        };
        loadHome();
    }, []);

    // Load Footer data
    useEffect(() => {
        const loadFooter = async () => {
            try {
                const res = await getFooter();
                const raw = res?.data?.data ?? [];
                const footerData = Array.isArray(raw) ? raw[0] : raw;
                setFooter(footerData || null);
            } catch (error) {
                console.error("Failed to load footer:", error);
            }
        };
        loadFooter();
    }, []);

    // Intersection Observer for fade-in animation
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            { threshold: 0.15 }
        );
        if (footerRef.current) {
            observer.observe(footerRef.current);
        }
        return () => {
            if (footerRef.current) {
                observer.unobserve(footerRef.current);
            }
            observer.disconnect();
        };
    }, []);

    // Email address – sourced from environment variable (or separate service)
    // Change this to your project's actual source (e.g., from a contact API)
    const contactEmail = process.env.REACT_APP_CONTACT_EMAIL || "contact@example.com";

    return (
        <footer
            ref={footerRef}
            className={`bg-[#0C121D] pt-4 sm:pt-5 pb-24 lg:pb-5 border-t border-slate-600/50 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
                }`}
        >
            <div className="max-w-full mx-auto px-3 sm:px-4 lg:px-20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-5 pb-3">
                    {/* Left Column – Brand & Social */}
                    <div className="flex flex-col items-start">
                        <a href="/" aria-label="Home" className="text-lg sm:text-xl font-bold tracking-wide">
                            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                                {footer?.footername}
                            </span>
                        </a>

                        <p className="mt-1.5 max-w-xs text-slate-400 text-xs leading-snug">
                            {footer?.footerdes}
                        </p>

                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {footer?.socialLinks
                                ?.filter((link) => link.showIcon)
                                .map((link) => {
                                    const Icon = icons[link.icon];

                                    if (!Icon) return null;

                                    return (
                                        <a
                                            key={link.id || link.href}
                                            href={link.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={link.text || link.title}
                                            className="relative group flex items-center justify-center w-9 h-9 rounded-lg
             bg-slate-800/70 border border-transparent
             transition-all duration-300
             hover:-translate-y-1 hover:bg-slate-800"
                                            style={{
                                                "--icon-color": link.iconColor || "#94a3b8",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.borderColor = link.iconColor || "#94a3b8";
                                                e.currentTarget.style.boxShadow = `0 0 16px ${link.iconColor || "#94a3b8"
                                                    }`;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.borderColor = "transparent";
                                                e.currentTarget.style.boxShadow = "none";
                                            }}
                                        >
                                            <Icon
                                                className="w-4 h-4 transition-all duration-300
               group-hover:scale-125"
                                                style={{
                                                    color: link.iconColor || "#94a3b8",
                                                }}
                                            />

                                            <span
                                                className="
    absolute
    bottom-full
    left-1/2
    -translate-x-1/2
    mb-2

    w-fit
    max-w-[220px]

    px-3
    py-1.5

    rounded-lg
    text-white

    text-xs
    font-semibold
    whitespace-nowrap
    leading-none

    shadow-lg

    opacity-0
    translate-y-1
    group-hover:opacity-100
    group-hover:translate-y-0

    transition-all
    duration-200
    ease-out

    pointer-events-none
    z-50
  "
                                                style={{
                                                    backgroundColor: link.iconColor || "#94a3b8",
                                                }}
                                            >
                                                {link.text || link.title || link.name}

                                                <span
                                                    className="
      absolute
      top-full
      left-1/2
      -translate-x-1/2

      w-0
      h-0

      border-l-[6px]
      border-r-[6px]
      border-t-[6px]

      border-l-transparent
      border-r-transparent
    "
                                                    style={{
                                                        borderTopColor: link.iconColor || "#94a3b8",
                                                    }}
                                                />
                                            </span>
                                        </a>
                                    );
                                })}

                            <EmailLink
                                email={contactData?.email}
                                icon={
                                    <icons.Mail
                                        className="w-4 h-4 transition-all duration-300 group-hover:scale-125"
                                    />
                                }
                                className="relative group flex items-center justify-center w-9 h-9 rounded-lg
             bg-slate-800/70 text-slate-400
             border border-transparent
             transition-all duration-300
             hover:-translate-y-1 hover:bg-slate-800 hover:text-cyan-300"
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = "#67e8f9";
                                    e.currentTarget.style.boxShadow = "0 0 16px #67e8f9";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = "transparent";
                                    e.currentTarget.style.boxShadow = "none";
                                }}
                            >
                                <span
                                    className="
    absolute
    bottom-full
    left-1/2
    -translate-x-1/2
    mb-2

    inline-block
    w-fit
    max-w-[220px]

    px-3
    py-1.5

    rounded-lg
    text-white
    text-xs
    font-semibold
    whitespace-nowrap
    leading-none

    shadow-lg

    opacity-0
    translate-y-1
    group-hover:opacity-100
    group-hover:translate-y-0

    transition-all
    duration-200
    ease-out

    pointer-events-none
    z-50
  "
                                    style={{
                                        backgroundColor: "#37faf4",
                                        color: "#000000",
                                    }}
                                >
                                    Email

                                    <span
                                        className="
      absolute
      top-full
      left-1/2
      -translate-x-1/2
      w-0
      h-0
      border-l-[6px]
      border-r-[6px]
      border-t-[6px]
      border-l-transparent
      border-r-transparent
    "
                                        style={{
                                            borderTopColor: "#37faf4",
                                        }}
                                    />
                                </span>
                            </EmailLink>
                        </div>
                    </div>

                    {/* Right Columns – Footer Sections */}
                    <div className="w-full md:col-span-2">
                        <div className="grid grid-cols-2 md:grid-cols-[repeat(auto-fit,_minmax(120px,_1fr))] gap-x-3 gap-y-2.5 sm:gap-x-4 sm:gap-y-3">
                            {footer?.footerSections?.map((section, index) => (
                                <div key={section.id || index} className="min-w-0">
                                    <h3 className="text-xs font-semibold text-white mb-1.5 tracking-wide">
                                        {section.heading}
                                    </h3>

                                    <ul className="space-y-1">
                                        {section.links
                                            ?.filter((link) => link.showFooter)
                                            .map((link, linkIndex) => (
                                                <li key={link.id || linkIndex}>
                                                    <a
                                                        href={
                                                            link.type === "route"
                                                                ? link.path
                                                                : `#${link.target}`
                                                        }
                                                        onClick={(e) => {
                                                            if (link.type === "projects") {
                                                                // Projects + Publications + Community filters
                                                                handlePortfolioNavigation(e, link);
                                                            } else if (link.type === "scroll") {
                                                                // Normal section navigation
                                                                e.preventDefault();
                                                                handleSmoothScroll(e, link.target);
                                                            }
                                                        }}
                                                        className="inline-block text-xs text-slate-400 hover:text-indigo-400 transition-all duration-300"
                                                    >
                                                        {link.label}
                                                    </a>
                                                </li>
                                            ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bottom Bar – Copyright & Developer */}
                <div className="pt-6 sm:pt-4 mt-6 sm:mt-4 pb-4 md:pb-0 border-t border-slate-700 flex flex-col md:flex-row items-center justify-between gap-2 md:gap-1.5 text-xs">                    <p className="text-slate-500 text-center md:text-left leading-snug">
                    © {new Date().getFullYear()} {footer?.copyright}
                </p>

                    <p className="text-slate-500 text-center flex items-center justify-center leading-snug">
                        <span>{footer?.developerDetail?.trim()}</span>
                        <a
                            href={footer?.footerLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center"
                        >
                            {home?.footerLogo ? (
                                <img
                                    src={home.footerLogo}
                                    alt="Developer Logo"
                                    className="h-8 md:h-10 w-auto object-contain"
                                />
                            ) : (
                                footer?.footername && (
                                    <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent font-semibold">
                                        {footer.footername}
                                    </span>
                                )
                            )}
                        </a>
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;